import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DateTime } from 'luxon';
import axios from 'axios';
import language from '../../variables/language';
import IconClock from '../../components/icons/IconClock';
import IconGoogleCalendar from '../../components/icons/IconGoogleCalendar';
import IconSudent from '../../components/icons/IconSudent';
import LessonDetailModal from '../students/LessonDetailModal';

// Hover-expand card for a lesson tile - mounts at the tile's own
// getBoundingClientRect (1x), then on the next frame switches to a 4x-sized
// rect centered on that same spot, so CSS transitions on left/top/width/
// height animate a smooth "grow from the tile" effect. Real layout
// dimensions (not transform:scale) on purpose - scaling would blur the
// bigger text and can't host genuinely new content (participants' full
// names, price, notes) the compact tile never had room for. Portaled to
// document.body because `.main-block` (App.css) clips overflow, so an
// in-place 4x tile would just get cut off.
function ExpandedLessonTile({ lesson, rect, participantNames, onEnter, onLeave, onOpen, lang }) {
   const [grown, setGrown] = useState(false);
   useEffect(() => {
      const raf = requestAnimationFrame(() => setGrown(true));
      return () => cancelAnimationFrame(raf);
   }, []);

   const scale = 4;
   const targetWidth = Math.min(rect.width * scale, window.innerWidth - 16);
   const targetHeight = rect.height * scale;
   const targetLeft = Math.min(Math.max(8, rect.left - (targetWidth - rect.width) / 2), window.innerWidth - targetWidth - 8);
   const targetTop = Math.min(Math.max(8, rect.top - (targetHeight - rect.height) / 2), window.innerHeight - targetHeight - 8);

   const style = grown
      ? { left: targetLeft, top: targetTop, width: targetWidth, height: targetHeight }
      : { left: rect.left, top: rect.top, width: rect.width, height: rect.height };

   const who = lesson.groupe ? lesson.groupe.name : participantNames.join(', ');

   return createPortal(
      <div className={'daily-lesson-expanded ' + lessonStatusClassOf(lesson)} style={style}
         onMouseEnter={onEnter} onMouseLeave={onLeave} onClick={onOpen}>
         <div className='daily-lesson-expanded-time'>{lesson.start} – {lesson.end}</div>
         {who ? (
            <div className='daily-lesson-expanded-who'>
               <IconSudent style={{ width: 15, height: 15, fill: 'var(--secondarytext-color)' }} />
               <span>{who}</span>
            </div>
         ) : ''}
         {lesson.course ? <div className='daily-lesson-expanded-row'><b>{lang.course}:</b> {lesson.course}</div> : ''}
         {lesson.price ? <div className='daily-lesson-expanded-row'><b>{lang.price}:</b> {lesson.price}</div> : ''}
         {lesson.comment ? <div className='daily-lesson-expanded-row daily-lesson-expanded-comment'><b>{lang.notes}:</b> {lesson.comment}</div> : ''}
         {lesson.googleEventHtmlLink ? (
            <a className='daily-lesson-expanded-calendar-link' href={lesson.googleEventHtmlLink} target='_blank' rel='noopener noreferrer'
               onClick={(e) => e.stopPropagation()}>
               <IconGoogleCalendar fillColor='icon-red' style={{ width: 14, height: 14 }} /> Google Calendar
            </a>
         ) : ''}
      </div>,
      document.body
   );
}

const lessonStatusClassOf = (lesson) => {
   const nowMinutes = DateTime.now().hour * 60 + DateTime.now().minute;
   if (nowMinutes >= lesson.startMinutes && nowMinutes <= lesson.endMinutes) return 'daily-lesson-current';
   if (nowMinutes > lesson.endMinutes) return 'daily-lesson-past';
   return 'daily-lesson-upcoming';
};

const RANGE_DEFAULT_START = 8 * 60;
const RANGE_DEFAULT_END = 22 * 60;
// One teacher never has two lessons at the same time in real use, so the
// track is always a single fixed-height row - no lane-stacking needed.
const ROW_HEIGHT = 64;
const LESSON_HEIGHT = 50;
const LESSON_TOP = (ROW_HEIGHT - LESSON_HEIGHT) / 2;
// this app's language.lang values ('en'/'ua'/'pl') aren't all valid Intl/
// Luxon locale codes as-is (Ukrainian is 'uk', not 'ua') - map them so
// DateTime.setLocale gets real full weekday/month names (with correct
// grammatical case, e.g. Ukrainian's genitive "12 серпня" not "12 Серпень")
// from ICU data instead of a hand-maintained, easy-to-get-wrong translation array.
const LUXON_LOCALE_BY_LANG = { en: 'en', ua: 'uk', pl: 'pl' };

const toMinutes = (hhmm) => {
   if (!hhmm) return null;
   const [h, m] = hhmm.split(':').map(Number);
   return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

const participantSummary = (names) => {
   if (names.length === 0) return '';
   if (names.length <= 2) return names.join(', ');
   return names.slice(0, 2).join(', ') + ' +' + (names.length - 2);
};

// same weekday-color convention as LessonDetailModal's header (Mon-Fri
// green, Saturday amber, Sunday red) - reused here rather than reinvented.
const weekdayColorClass = (isoWeekday) => {
   if (isoWeekday === 6) return 'daily-schedule-weekday-saturday';
   if (isoWeekday === 7) return 'daily-schedule-weekday-sunday';
   return 'daily-schedule-weekday-workday';
};

const DailySchedule = () => {
   const lang = language[language.lang];
   const today = DateTime.now().toFormat('yyyy-MM-dd');

   const [lessons, setLessons] = useState([]);
   const [participantsByLesson, setParticipantsByLesson] = useState({});
   const [participantsByLessonFull, setParticipantsByLessonFull] = useState({});
   const [status, setStatus] = useState('loading');
   const [selectedLessonId, setSelectedLessonId] = useState(null);
   const [modalOpen, setModalOpen] = useState(false);
   const [refresh, setRefresh] = useState(0);
   const [, setTick] = useState(0);
   const [hoveredLesson, setHoveredLesson] = useState(null);
   const [hoverRect, setHoverRect] = useState(null);
   const hoverTimeoutRef = useRef(null);

   // keeps the "now" line and past/current/upcoming coloring fresh while the page stays open
   useEffect(() => {
      const interval = setInterval(() => setTick((t) => t + 1), 60000);
      return () => clearInterval(interval);
   }, []);

   useEffect(() => {
      setStatus('loading');
      Promise.all([
         axios.get('/api/lessons', { params: { date: today }, withCredentials: true }),
         axios.get('/api/events', { params: { dateFrom: today, dateTo: today }, withCredentials: true }),
      ]).then(([lessonsRes, eventsRes]) => {
         setLessons(Array.isArray(lessonsRes.data) ? lessonsRes.data : []);
         const events = Array.isArray(eventsRes.data) ? eventsRes.data : [];
         const byLesson = {};
         const byLessonFull = {};
         events.forEach((ev) => {
            if (!ev.lesson || !ev.student) return;
            const key = ev.lesson.id;
            // abbreviated - first initial + full surname (e.g. "S. Sapronov"),
            // keeps lesson blocks readable even when compressed narrow. The
            // hover-expanded card has room to spare, so it gets full names.
            const initial = ev.student.name ? ev.student.name.charAt(0).toUpperCase() + '.' : '';
            const name = [initial, ev.student.surname].filter(Boolean).join(' ');
            const fullName = [ev.student.name, ev.student.surname].filter(Boolean).join(' ');
            if (name) {
               if (!byLesson[key]) byLesson[key] = [];
               byLesson[key].push(name);
            }
            if (fullName) {
               if (!byLessonFull[key]) byLessonFull[key] = [];
               byLessonFull[key].push(fullName);
            }
         });
         setParticipantsByLesson(byLesson);
         setParticipantsByLessonFull(byLessonFull);
         setStatus('done');
      }).catch(() => setStatus('done'));
   }, [today, refresh]);

   const openLesson = (id) => {
      setSelectedLessonId(id);
      setModalOpen(true);
   };

   const { hourTicks, positionedLessons, nowPercent, showNowLine, stats } = useMemo(() => {
      const withTimes = lessons
         .map((lesson) => {
            const startMinutes = toMinutes(lesson.start);
            const endMinutes = toMinutes(lesson.end) ?? (startMinutes != null ? startMinutes + (lesson.duration || 60) : null);
            return { ...lesson, startMinutes, endMinutes };
         })
         .filter((lesson) => lesson.startMinutes != null && lesson.endMinutes != null);

      const starts = withTimes.map((l) => l.startMinutes);
      const ends = withTimes.map((l) => l.endMinutes);
      const rangeStart = Math.min(RANGE_DEFAULT_START, ...(starts.length ? starts : [RANGE_DEFAULT_START]));
      const rangeEnd = Math.max(RANGE_DEFAULT_END, ...(ends.length ? ends : [RANGE_DEFAULT_END]));
      const totalRange = Math.max(1, rangeEnd - rangeStart);

      const ticks = [];
      const firstHour = Math.floor(rangeStart / 60);
      const lastHour = Math.ceil(rangeEnd / 60);
      for (let h = firstHour; h <= lastHour; h++) {
         const fromStart = h * 60 - rangeStart;
         if (fromStart < 0 || fromStart > totalRange) continue;
         ticks.push({ hour: h, label: String(h).padStart(2, '0') + ':00', percent: (fromStart / totalRange) * 100 });
      }
      // the last tick's label would otherwise overflow past the container's
      // right edge (its text starts AT 100% and flows further right) -
      // anchor it from the right instead so it stays within bounds.
      if (ticks.length > 0) ticks[ticks.length - 1].alignEnd = true;

      const positioned = withTimes.map((lesson) => ({
         ...lesson,
         leftPercent: ((lesson.startMinutes - rangeStart) / totalRange) * 100,
         widthPercent: ((lesson.endMinutes - lesson.startMinutes) / totalRange) * 100,
      })).sort((a, b) => a.startMinutes - b.startMinutes);

      const nowMinutes = DateTime.now().hour * 60 + DateTime.now().minute;
      const inRange = nowMinutes >= rangeStart && nowMinutes <= rangeEnd;

      const statCounts = { past: 0, current: 0, upcoming: 0 };
      withTimes.forEach((lesson) => {
         if (nowMinutes >= lesson.startMinutes && nowMinutes <= lesson.endMinutes) statCounts.current++;
         else if (nowMinutes > lesson.endMinutes) statCounts.past++;
         else statCounts.upcoming++;
      });

      return {
         hourTicks: ticks,
         positionedLessons: positioned,
         nowPercent: ((nowMinutes - rangeStart) / totalRange) * 100,
         showNowLine: inRange,
         stats: statCounts,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [lessons]);

   // debounced both ways: a short delay before showing (fast mouse passes
   // over several tiles in a row shouldn't each pop a card) and before
   // hiding (moving from the tile onto the - overlapping, centered-grow -
   // expanded card itself must not read as "left the hover area").
   const scheduleShowExpanded = (lesson, rect) => {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
         setHoveredLesson(lesson);
         setHoverRect(rect);
      }, 150);
   };
   const scheduleHideExpanded = () => {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
         setHoveredLesson(null);
         setHoverRect(null);
      }, 120);
   };
   const cancelHideExpanded = () => clearTimeout(hoverTimeoutRef.current);

   const todayDt = DateTime.now().setLocale(LUXON_LOCALE_BY_LANG[language.lang] || 'en');
   const statsTooltip = stats
      ? [
           stats.past ? stats.past + ' ' + lang.dailyScheduleStatPast : '',
           stats.current ? stats.current + ' ' + lang.dailyScheduleStatCurrent : '',
           stats.upcoming ? stats.upcoming + ' ' + lang.dailyScheduleStatUpcoming : '',
        ].filter(Boolean).join(' · ')
      : '';

   return (
      <div className='daily-schedule-container dashboard-component'>
         <div className='daily-schedule-header'>
            <IconClock style={{
               width: 28, height: 28, fill: 'var(--dashboard-header-icon-color)',
               filter: 'drop-shadow(0 0 3px rgba(99, 218, 200, 0.65)) drop-shadow(0 0 9px rgba(99, 218, 200, 0.35))',
            }} />
            <div className='daily-schedule-header-text'>
               <p className='daily-schedule-title'>{lang['Daily schedule by']}</p>
               <span className={'daily-schedule-weekday ' + weekdayColorClass(todayDt.weekday)}>{todayDt.toFormat('cccc')}</span>
               <span className='daily-schedule-date'>{todayDt.toFormat('d MMMM yyyy')}</span>
            </div>
            <div className='daily-schedule-header-right'>
               {status === 'loading' ? <i className='daily-schedule-spinner' title={lang['Loading ...']}></i> : ''}
               {status === 'done' && lessons.length > 0 ? (
                  <span className='daily-schedule-count' title={statsTooltip}>{lessons.length}</span>
               ) : ''}
               <span className='daily-schedule-live-time'>
                  <i className='daily-schedule-live-dot'></i>
                  {todayDt.toFormat('HH:mm')}
               </span>
            </div>
         </div>

         {/* The ruler+track structure renders in EVERY state (loading, empty,
             populated) at the same fixed ROW_HEIGHT (a teacher never has two
             lessons at once, so there's never more than one row), so the
             block never flattens down to a bare text line and then jumps
             back to full size once data lands - only what's drawn inside
             the track changes: a single glowing loading bar while loading,
             a centered empty-state message overlaid on the (still
             full-height) empty track, or the real lesson blocks. */}
         <div className='daily-schedule-inner'>
            <div className='daily-schedule-ruler'>
               {hourTicks.map((t) => (
                  <div key={t.hour} className={'daily-schedule-tick' + (t.alignEnd ? ' daily-schedule-tick-last' : '')} style={{ left: t.percent + '%' }}>
                     <span>{t.label}</span>
                  </div>
               ))}
            </div>
            <div className='daily-schedule-track' style={{ height: ROW_HEIGHT + 'px' }}>
               {status === 'loading' ? (
                  <div className='daily-schedule-loading-bar'>
                     <div className='daily-schedule-loading-bar-shine'></div>
                  </div>
               ) : ''}
               {status === 'done' && lessons.length === 0 ? (
                  <p className='daily-schedule-empty daily-schedule-empty-overlay'>{lang.noLessonsToday}</p>
               ) : ''}
               {status === 'done' && showNowLine ? (
                  <div className={'daily-schedule-now-line' + (nowPercent > 90 ? ' daily-schedule-now-line-end' : '')} style={{ left: nowPercent + '%' }}>
                     <span>{DateTime.now().toFormat('HH:mm')}</span>
                  </div>
               ) : ''}
               {status === 'done' ? positionedLessons.map((lesson, index) => {
                  const names = participantsByLesson[lesson.id] || [];
                  const who = lesson.groupe ? lesson.groupe.name : participantSummary(names);
                  return (
                     <div key={lesson.id}
                        className={'daily-lesson ' + lessonStatusClassOf(lesson)}
                        style={{ left: lesson.leftPercent + '%', width: lesson.widthPercent + '%', top: LESSON_TOP + 'px', '--i': index }}
                        onClick={() => openLesson(lesson.id)}
                        onMouseEnter={(e) => scheduleShowExpanded(lesson, e.currentTarget.getBoundingClientRect())}
                        onMouseLeave={scheduleHideExpanded}
                        title={[lesson.start + '–' + lesson.end, who].filter(Boolean).join(' · ')}>
                        <div className='daily-lesson-time'>{lesson.start} – {lesson.end}</div>
                        {who ? (
                           <div className='daily-lesson-who'>
                              <IconSudent style={{ width: 12, height: 12, fill: 'var(--secondarytext-color)' }} />
                              <span>{who}</span>
                           </div>
                        ) : ''}
                        {lesson.googleEventHtmlLink ? (
                           <a className='daily-lesson-calendar-link' href={lesson.googleEventHtmlLink} target='_blank' rel='noopener noreferrer'
                              onClick={(e) => e.stopPropagation()} title='Google Calendar'>
                              <IconGoogleCalendar fillColor='icon-red' style={{ width: 14, height: 14 }} />
                           </a>
                        ) : ''}
                     </div>
                  );
               }) : ''}
            </div>
         </div>

         {hoveredLesson && hoverRect ? (
            <ExpandedLessonTile lesson={hoveredLesson} rect={hoverRect} lang={lang}
               participantNames={participantsByLessonFull[hoveredLesson.id] || []}
               onEnter={cancelHideExpanded} onLeave={scheduleHideExpanded}
               onOpen={() => { setHoveredLesson(null); setHoverRect(null); openLesson(hoveredLesson.id); }} />
         ) : ''}

         <LessonDetailModal isOpen={modalOpen} lessonId={selectedLessonId}
            onClose={() => setModalOpen(false)}
            onSaved={() => { setModalOpen(false); setRefresh((r) => r + 1); }}
            onParticipantsChanged={() => setRefresh((r) => r + 1)} />
      </div>
   );
};

export default DailySchedule;
